import { cn } from '@/lib/utils';
import type { InstallationType } from '@/services/data/shootingPreferences';
import installationFauteuil from '@/assets/installation-fauteuil.png';
import installationTabouret from '@/assets/installation-tabouret.png';
import installationBureau from '@/assets/installation-bureau.png';

interface InstallationChoiceProps {
  readonly selected: InstallationType | null;
  readonly onSelect: (type: InstallationType | null) => void;
  readonly onContinue: () => void;
}

const installations: { type: InstallationType; label: string; image: string }[] = [
  {
    type: 'fauteuil',
    label: 'Fauteuil',
    image: installationFauteuil,
  },
  {
    type: 'tabouret',
    label: 'Tabouret',
    image: installationTabouret,
  },
  {
    type: 'bureau',
    label: 'Bureau',
    image: installationBureau,
  },
];

export function InstallationChoice({ selected, onSelect, onContinue }: InstallationChoiceProps) {
  return (
    <div className="flex-1 flex items-center justify-center -mx-6 w-[calc(100%+3rem)]">
      <div className="flex items-center justify-start gap-4 overflow-x-auto pb-4 snap-x snap-mandatory w-full scroll-pl-6">
        {/* Spacer for initial left alignment */}
        <div className="w-6 flex-shrink-0" aria-hidden="true" />
        {installations.map((installation) => (
          <button
            key={installation.type}
            onClick={() => onSelect(selected === installation.type ? null : installation.type)}
            className="relative flex flex-col items-center flex-shrink-0 snap-start group h-full"
          >
            {/* Image container - adapts to available height */}
            <div className={cn(
              "relative bg-black rounded-xl overflow-hidden transition-all h-full max-h-[50vh] md:max-h-[62vh] aspect-[9/16]",
              selected === installation.type
                ? "opacity-100"
                : "opacity-80 md:group-hover:opacity-100"
            )}>
              <img 
                src={installation.image} 
                alt={installation.label}
                className="w-full h-full object-cover object-top"
              />
              
              {/* Label with radio button positioned top-left */}
              <div className="absolute top-2 left-2 flex items-center gap-2 px-2 py-1 rounded bg-black/50 backdrop-blur-sm">
                {/* Radio button */}
                <div className={cn(
                  "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors",
                  selected === installation.type 
                    ? "border-warning bg-warning" 
                    : "border-white/60"
                )}>
                  {selected === installation.type && (
                    <div className="w-1.5 h-1.5 rounded-full bg-black" />
                  )}
                </div>
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  selected === installation.type ? "text-warning" : "text-white/90"
                )}>
                  {installation.label}
                </span>
              </div>
            </div>
          </button>
        ))}
        {/* Spacer for right edge */}
        <div className="w-6 flex-shrink-0" aria-hidden="true" />
      </div>
    </div>
  );
}
